package com.pincer.core.rules

import com.pincer.core.InvalidData
import com.pincer.core.model.tree.Candidate

public val DEFINITELY_POSSIBLE = 1F
public val MIGHT_BE_POSSIBLE = 0.5F
public val DEFINITELY_NOT_POSSIBLE = 0F

typealias RuleBuilder = (String,List<String>) -> Rule?

abstract class Rule() {

    abstract fun score(candidate: Candidate): Float

    companion object {

        fun parseRules(
            string: String,
            cache: MutableMap<String,Rule>? = null,
            injected: RuleBuilder? = null,
        ): Rule {
            if (cache != null && cache[string] != null) return cache[string]!!
            val rules = mutableSetOf<Rule>()
            var nextTermIsBoolean = false
            var booleanTerms: String? = null
            var cursor = 0
            var termLength = 0
            var brackets = 0
            while (cursor + termLength < string.length) {
                var endOfTerm = false
                val c = string[cursor+termLength]
                if (c == '(') {
                    if (brackets == 0) {
                        cursor++ // trim outside pair
                    }
                    else {
                        termLength++
                    }
                    brackets++
                }
                else if (c == ')') {
                    if (brackets == 0) throw InvalidData("It looks like there's something wrong with how the rules are nested")
                    brackets--
                    if (brackets == 0) {
                        endOfTerm = true
                    }
                    else {
                        termLength++
                    }
                }
                else if (brackets > 0) {
                    termLength++
                }
                else if (c == ' ') {
                    if (termLength == 0) {
                        cursor++ // skip double or leading spaces
                    }
                    else {
                        endOfTerm = true
                    }
                }
                else {
                    termLength++
                }
                if (cursor + termLength == string.length) { // we've reached end of the string
                    if (brackets > 0) throw InvalidData("It looks like there's something wrong with how the rules are nested")
                    endOfTerm = true
                }
                if (endOfTerm) {
                    if (termLength > 0) {
                        val term = string.substring(cursor, cursor + termLength)
                        if (nextTermIsBoolean) {
                            if (!(term == "AND" || term == "OR")) {
                                throw InvalidData("The rules have an unexpected boolean term : $term")
                            }
                            if (booleanTerms == null) {
                                booleanTerms = term
                            }
                            else if (term != booleanTerms) {
                                throw InvalidData("The rules are using a mix of boolean terms")
                            }
                            nextTermIsBoolean = false
                        }
                        else {
                            var rule = if (c == ')') parseRules(string = term, injected = injected) else parseRule(string = term, injected = injected)
                            rules.add(rule)
                            nextTermIsBoolean = true
                        }
                    }
                    cursor = cursor + termLength + 1
                    termLength = 0
                }
            }
            val build =           if (rules.size == 0) FixedRule(0F)
                        else if (booleanTerms == null) rules.first()
                        else if (booleanTerms == "OR") OrRule(rules)
                                                  else AndRule(rules)

            if (cache != null) cache.put(string, build)
            return build
        }

        private val ruleRegex = Regex("^([a-z][a-zA-Z]*|\\d+(?:\\.?\\d+)?)(?:\\[([^ \\]]+)\\])?$")

        internal fun parseRule(
                string: String,
                injected: RuleBuilder? = null,
            ): Rule {
            val match = ruleRegex.find(string)
            if (match == null) throw InvalidData("Could not parse this rule : $string")
            val (ruleName, paramList) = match.destructured
            val params = paramList.split(",")
            // Four ways to match the rule name:
            // 1. Rules defined in core ...
            when (ruleName) {
                "duration"            -> return            DurationRule.parse(params)
                "durationFromTo"      -> return      DurationFromToRule.parse(params)
                "durationDivisibleBy" -> return DurationDivisibleByRule.parse(params)
                "participant"         -> return         ParticipantRule.parse(params)
                "idealHeadcount"      -> return      IdealHeadcountRule.parse(params)
            }
            // 2. It is just a number ...
            try {
                return FixedRule(string.toFloat())
            }
            catch (ignored: NumberFormatException) { }
            if (injected != null) {
                // 3. Server-side injected ...
                val serverRule = injected.invoke(ruleName, params)
                if (serverRule != null) return serverRule
                throw InvalidData("Did not recognise this rule: $ruleName")
            }
            else {
                // 4. No injection. Likely a client-side validation call.
                return FixedRule(1F)
            }
        }

    }
}

